import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './DonateOptionsModal.css';

interface DonateOptionsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const DonateOptionsModal: React.FC<DonateOptionsModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    if (!isOpen) return null;

    const handleOptionClick = (path: string) => {
        navigate(path);
        onClose();
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="donate-options-overlay" onClick={handleOverlayClick}>
            <div className="donate-options-modal">
                <button className="modal-close-button" onClick={onClose} aria-label="Close modal">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="modal-header">
                    <h2 className="modal-title">{t('donateModal.title', 'Choose Donation Option')}</h2>
                    <p className="modal-subtitle">{t('donateModal.subtitle', 'Select how you would like to proceed')}</p>
                </div>

                <div className="donate-options-container">
                    <button
                        className="donate-option-card login-card"
                        onClick={() => handleOptionClick('/login')}
                    >
                        <div className="option-icon-wrapper">
                            <svg className="option-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="24" fill="#EEF2FF" />
                                <path d="M24 26C27.3137 26 30 23.3137 30 20C30 16.6863 27.3137 14 24 14C20.6863 14 18 16.6863 18 20C18 23.3137 20.6863 26 24 26Z" fill="#4F46E5" />
                                <path d="M14 34C14 29.5817 17.5817 26 22 26H26C30.4183 26 34 29.5817 34 34" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <h3 className="option-title">{t('donateModal.login.title', 'Login')}</h3>
                        <p className="option-description">
                            {t('donateModal.login.description', 'Sign in to your existing account')}
                        </p>
                        <div className="option-arrow">→</div>
                    </button>

                    <button
                        className="donate-option-card register-card"
                        onClick={() => handleOptionClick('/register')}
                    >
                        <div className="option-icon-wrapper">
                            <svg className="option-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="24" fill="#F0FDF4" />
                                <path d="M18 20H30M18 24H30M18 28H25" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
                                <rect x="16" y="14" width="16" height="20" rx="2" stroke="#10B981" strokeWidth="2" />
                            </svg>
                        </div>
                        <h3 className="option-title">{t('donateModal.register.title', 'Register')}</h3>
                        <p className="option-description">
                            {t('donateModal.register.description', 'Create a new account to track your donations')}
                        </p>
                        <div className="option-arrow">→</div>
                    </button>

                    <button
                        className="donate-option-card anonymous-card"
                        onClick={() => handleOptionClick('/donate-anonymously')}
                    >
                        <div className="option-icon-wrapper">
                            <svg className="option-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="24" fill="#FFF7ED" />
                                <path d="M24 34C29.5228 34 34 29.5228 34 24C34 18.4772 29.5228 14 24 14C18.4772 14 14 18.4772 14 24C14 29.5228 18.4772 34 24 34Z" stroke="#F59E0B" strokeWidth="2" />
                                <path d="M24 18V24L28 28" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <h3 className="option-title">{t('donateModal.anonymous.title', 'Donate Anonymously')}</h3>
                        <p className="option-description">
                            {t('donateModal.anonymous.description', 'Make a donation without creating an account')}
                        </p>
                        <div className="option-arrow">→</div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DonateOptionsModal;
